import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/contexts/language/LanguageProvider";
import { TournamentProvider } from "@/contexts/tournament/TournamentProvider";
import { SupabaseEventProvider } from "@/contexts/event/SupabaseEventProvider";
import { AuthProvider } from "@/contexts/auth/AuthProvider";
import {
  authProviderNameForPath,
  authRoutePrefixForPath,
} from "@/config/auth";
import { SupabaseClientProvider } from "@/contexts/supabase/SupabaseClientProvider";
import { PenaltyProvider } from "@/contexts/penalty/PenaltyProvider";
import { MessageProvider } from "@/contexts/message/MessageProvider";
import { GroupProvider } from "@/contexts/GroupContext";
import { NotificationProvider } from "@/contexts/notification/NotificationProvider";
import { NotificationDataProvider } from "@/contexts/notification/NotificationDataProvider";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import LandingPage from "./pages/LandingPage";
import Profile from "./pages/Profile";
import TournamentDetail from "./pages/TournamentDetail";
import TournamentResults from "./pages/TournamentResults";
import EventDetail from "./pages/EventDetail";
import AmericanoTournamentPage from "./pages/AmericanoTournamentPage";
import MexicanoTournamentPage from "./pages/MexicanoTournamentPage";
import PastTournaments from "./pages/PastTournaments";
import { AppLayout } from "@/components/layout/AppLayout";
import Dashboard from "./pages/Dashboard";
import Calendar from "./pages/Calendar";
import Groups from "./pages/Groups";
import Members from "./pages/Members";
import { ProtectedAdminRoute } from "@/components/admin/ProtectedAdminRoute";
import Tournaments from "./pages/Tournaments";
import Notifications from "./pages/Notifications";
import Messages from "./pages/Messages";
import Rankings from "./pages/Rankings";
import { lazy, Suspense } from "react";

// Lazy load admin page to reduce initial bundle size
const Admin = lazy(() => import("./pages/Admin"));
const Auth0AuthProvider = lazy(() =>
  import("@/contexts/auth/Auth0AuthProvider").then((module) => ({
    default: module.Auth0AuthProvider,
  })),
);

const queryClient = new QueryClient();

function routePath(path: string, routePrefix: string) {
  if (!routePrefix) return path;
  if (path === "/") return routePrefix;
  if (path === "*") return `${routePrefix}/*`;
  return `${routePrefix}${path}`;
}

function AuthRuntimeProviders({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const providerName = authProviderNameForPath(location.pathname);
  const SelectedAuthProvider = providerName === "auth0" ? Auth0AuthProvider : AuthProvider;

  return (
    <SupabaseClientProvider providerName={providerName}>
      <SelectedAuthProvider>{children}</SelectedAuthProvider>
    </SupabaseClientProvider>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const routePrefix = authRoutePrefixForPath(location.pathname);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage />;
  }

  return (
    <AppLayout>
      <ErrorBoundary>
      <Routes>
        <Route path={routePath("/", routePrefix)} element={<Dashboard />} />
        <Route path={routePath("/calendar", routePrefix)} element={<Calendar />} />
        <Route path={routePath("/notifications", routePrefix)} element={<Notifications />} />
        <Route path={routePath("/groups", routePrefix)} element={<Groups />} />
        <Route path={routePath("/subgroups", routePrefix)} element={<Groups />} />
        <Route path={routePath("/members", routePrefix)} element={<Members />} />
        <Route path={routePath("/profile", routePrefix)} element={<Profile />} />
        <Route path={routePath("/messages", routePrefix)} element={<Messages />} />
        <Route path={routePath("/tournaments", routePrefix)} element={<Tournaments />} />
        <Route path={routePath("/past-tournaments", routePrefix)} element={<PastTournaments />} />
        <Route path={routePath("/tournaments/rankings", routePrefix)} element={<Rankings />} />
        <Route path={routePath("/tournaments/:id", routePrefix)} element={<TournamentResults />} />
        <Route path={routePath("/americano", routePrefix)} element={<AmericanoTournamentPage />} />
        <Route path={routePath("/mexicano", routePrefix)} element={<MexicanoTournamentPage />} />
        <Route path={routePath("/tournament/:id", routePrefix)} element={<TournamentDetail />} />
        <Route path={routePath("/event/:id", routePrefix)} element={<EventDetail />} />
        <Route path={routePath("/admin", routePrefix)} element={
          <ProtectedAdminRoute fallback={<Navigate to={routePath("/", routePrefix)} replace />}> 
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-muted-foreground">{t("common.loading")}</p>
                </div>
              </div>
            }>
              <Admin />
            </Suspense>
          </ProtectedAdminRoute>
        } />
        <Route path={routePath("*", routePrefix)} element={<Dashboard />} />
      </Routes>
      </ErrorBoundary>
    </AppLayout>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <Suspense fallback={null}>
      <BrowserRouter>
        <AuthRuntimeProviders>
          <PenaltyProvider>
            <GroupProvider>
              <SupabaseEventProvider>
                <TournamentProvider>
                  <MessageProvider>
                    <NotificationProvider>
                    <NotificationDataProvider>
                      <TooltipProvider>
                        <Toaster />
                        <Sonner />
                        <AppContent />
                      </TooltipProvider>
                    </NotificationDataProvider>
                    </NotificationProvider>
                    </MessageProvider>
                  </TournamentProvider>
                </SupabaseEventProvider>
              </GroupProvider>
            </PenaltyProvider>
        </AuthRuntimeProviders>
      </BrowserRouter>
      </Suspense>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
